import type { AgentAvatar as AvatarShape } from "@codvero/shared";
import { cn } from "@/lib/cn.ts";

/** Agent avatar: photo when set, otherwise emoji on a tinted disc. */
export function AgentAvatar({ avatar, size = "md", className }: { avatar: AvatarShape | null | undefined; size?: "xs" | "sm" | "md" | "lg"; className?: string }) {
  const dim = size === "xs" ? "size-5 text-xs" : size === "sm" ? "size-7 text-base" : size === "lg" ? "size-16 text-3xl" : "size-10 text-lg";
  const color = avatar?.color ?? "#64748b";
  if (avatar?.image) return <img src={avatar.image} alt="" className={cn("shrink-0 rounded-full object-cover", dim, className)} style={{ boxShadow: `0 0 0 2px ${color}55` }} />;
  return <span className={cn("grid shrink-0 place-items-center rounded-full", dim, className)} style={{ background: color + "33" }}>{avatar?.emoji ?? "👥"}</span>;
}

/** Crops an image file to a square `px` PNG data URL (client-side, for small avatars). */
export async function cropToDataUrl(file: File, px = 96): Promise<string> {
  const bmp = await createImageBitmap(file);
  const side = Math.min(bmp.width, bmp.height);
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, px, px);
  bmp.close();
  let url = canvas.toDataURL("image/webp", 0.85);
  if (!url.startsWith("data:image/webp")) url = canvas.toDataURL("image/jpeg", 0.85);
  if (url.length > 150_000) url = canvas.toDataURL("image/jpeg", 0.6);
  return url;
}
